import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoreHorizontal,
  Eye,
  CheckCircle2,
  Trash2,
  Edit,
  Building2,
  User,
  Calendar
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { formatToIST, formatDateIST } from '../../utils/dateUtils';
import { cn } from '../../lib/utils';
import DealStageDropdown from '../../components/deals/DealStageDropdown';
import { usePrivacy } from '../../contexts/PrivacyContext';

const DealsTable = ({ 
  deals, 
  getStageInfo, 
  getEmployeeName, 
  canApprove, 
  canDelete, 
  onApprove, 
  onDelete, 
  onEdit,
  onRefresh
}) => {
  const navigate = useNavigate();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility

  return (
    <>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {deals.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No deals found matching your filters.</p>
          </div>
        ) : (
          deals.map((deal) => {
            const stageInfo = getStageInfo(deal.deal_stage);
            const isEditable = deal.deal_stage !== 'WON_PAYMENT_RECEIVED';
            return (
              <div 
                key={deal.id}
                className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 cursor-pointer active:bg-slate-50"
                onClick={() => navigate(`/deals/${deal.id}`)}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">{deal.company_name}</h3>
                    <p className="text-sm text-slate-500 truncate">{deal.poc_name || deal.contact_name}</p>
                  </div>
                  <Badge className={cn('text-xs flex-shrink-0', stageInfo?.color)}>
                    {stageInfo?.label || deal.deal_stage}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div className="flex items-center gap-1.5 text-slate-600">
                    <span className="text-slate-400">Value:</span>
                    <span className="font-semibold">{formatCurrency(deal.deal_value_estimate || deal.total_deal_value)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <User className="w-3.5 h-3.5" />
                    <span className="truncate">{getEmployeeName(deal.primary_employee_id)}</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2">
                  {/* Stage change on mobile for non-won deals */}
                  {!deal.deal_stage?.startsWith('WON') && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <DealStageDropdown
                        currentStage={deal.deal_stage}
                        dealId={deal.id}
                        onUpdate={onRefresh}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 h-8 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/deals/${deal.id}`);
                      }}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/deals/${deal.id}`);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>

                        {isEditable && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit && onEdit(deal);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Deal
                          </DropdownMenuItem>
                        )}

                        {deal.deal_stage === 'PENDING_ADMIN_APPROVAL' && canApprove && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onApprove && onApprove(deal);
                            }}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                            Approve
                          </DropdownMenuItem>
                        )}

                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600 focus:text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete && onDelete(deal);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-900">
              <TableHead className="w-[250px] font-semibold">Company</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Stage</TableHead>
              <TableHead className="text-right font-semibold">Value</TableHead>
              <TableHead className="text-center font-semibold">Probability</TableHead>
              <TableHead className="font-semibold">Owner</TableHead>
              <TableHead className="font-semibold">Created</TableHead>
              <TableHead className="w-[80px] text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-slate-500">
                  No deals found matching your filters.
                </TableCell>
              </TableRow>
            ) : (
              deals.map((deal) => {
                const stageInfo = getStageInfo(deal.deal_stage);
                // Check if editable: Not in WON_PAYMENT_RECEIVED
              const isEditable = deal.deal_stage !== 'WON_PAYMENT_RECEIVED';

              return (
                <TableRow 
                  key={deal.id}
                  className="hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => navigate(`/deals/${deal.id}`)}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2 font-medium text-slate-800 dark:text-slate-100">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {deal.company_name}
                      </div>
                      {deal.plan_name && (
                        <span className="text-xs text-slate-500 ml-6 mt-0.5">
                          {deal.plan_name}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <User className="w-3 h-3 text-slate-400" />
                      {deal.contact_name || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn('whitespace-nowrap', stageInfo.bgColor, stageInfo.textColor)}>
                      {stageInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(deal.deal_value_estimate)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-16 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all duration-500" 
                          style={{ width: `${deal.close_probability || 0}%` }} 
                        />
                      </div>
                      <span className="text-[10px] text-slate-500">{deal.close_probability || 0}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-[10px] font-bold text-blue-700 dark:text-blue-300">
                        {getEmployeeName(deal.primary_employee_id).substring(0,2).toUpperCase()}
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-300 truncate max-w-[100px]">
                        {getEmployeeName(deal.primary_employee_id)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDateIST(deal.created_at)}
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                        {/* Stage Jumper */}
                        <DealStageDropdown 
                            currentStage={deal.deal_stage} 
                            dealId={deal.id} 
                            onUpdate={onRefresh} 
                        />
                        
                        {/* Standard Actions */}
                        <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/deals/${deal.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                            </DropdownMenuItem>
                            
                            {isEditable && (
                            <DropdownMenuItem onClick={() => onEdit(deal)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Deal
                            </DropdownMenuItem>
                            )}

                            {deal.deal_stage === 'PENDING_ADMIN_APPROVAL' && canApprove && (
                            <DropdownMenuItem onClick={() => onApprove(deal)}>
                                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />
                                Approve
                            </DropdownMenuItem>
                            )}

                            {canDelete && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                className="text-red-600 focus:text-red-600"
                                onClick={() => onDelete(deal)}
                                >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                                </DropdownMenuItem>
                            </>
                            )}
                        </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
    </>
  );
};

export default DealsTable;
